/*
 *
 * Copyright (c) Vaisala Oyj. All rights reserved.
 *
 */

import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import {useAuth} from "../auth/AuthContext"



interface LidarsContextType {
    lidars: string[];
    getLidars: () => Promise<string[]>;
    createLidar: (lidar: string) => Promise<boolean>;
    removeLidar: (lidar: string) => Promise<boolean>;
}

const LidarsContext = createContext<LidarsContextType | undefined>(undefined);

const LidarsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const {secureFetch} = useAuth()
    const [lidars, setLidars] = useState<string[]>([]);

    useEffect(() => {
        getLidars().then(setLidars);
    }, []);

    const getLidars = async (): Promise<string[]> => {
        try {
            const response = await secureFetch(`/api/lidars`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const data = await response.json();
                setLidars(data);
                return data;
            } else {
                console.error('Failed to fetch lidars');
                return [];
            }
        } catch (error) {
            console.error('Error during fetch:', error);
            return [];
        }
    };

    const createLidar = async (lidar: string): Promise<boolean> => {
        try {
            const response = await secureFetch(`/api/lidars`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ "lidar_serial_number": lidar }),
            });

            if (response.ok) {
                setLidars((prev) => [...prev, lidar]);
                return true;
            } else {
                return false;
            }
        } catch (error) {
            console.error('Error during add:', error);
            return false;
        }
    };

    const removeLidar = async (lidar: string): Promise<boolean> => {
        try {
            const response = await secureFetch(`/api/lidars`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ "lidar_serial_number": lidar }),
            });

            if (response.ok) {
                setLidars((prev) => prev.filter((item) => item !== lidar));
                return true;
            } else {
                return false;
            }
        } catch (error) {
            console.error('Error during delete:', error);
            return false;
        }
    };

    return (
        <LidarsContext.Provider value={{ lidars, getLidars, createLidar, removeLidar }}>
            {children}
        </LidarsContext.Provider>
    );
};

const useLidars = (): LidarsContextType => {
    const context = useContext(LidarsContext);
    if (context === undefined) {
        throw new Error('useLidars must be used within a LidarsProvider');
    }
    return context;
};

export { LidarsProvider, useLidars };
